import React, { useState, ChangeEvent, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Autocomplete, TextField, Checkbox, FormControl, InputLabel } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { useLocation, useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, MessageAlert, FormInput } from '../../../components';
import { ButtonColor } from '../../../components/Button/types';
import { CommHandler } from '../../../utils';
import { config } from '../../../config';
import createFormValidator from '../../../utils/FormValidator';
import AddKRAFormWrapper from '../../KRA/Component/AddKRAForm.styled';
import { validateAndSanitizeNameWithLimitedSpecialCharacters } from '../../../utils/inputSanitizer';
import { getAuthHeader } from '../../../utils/utils';

type RequestData = {
  department: string;
  buIds: string[];
  id?: string;
  hrIds: string[];
};

const validationRules = {
  buName: 'required| string',
  buHead: 'required|array',
  hrBPs: 'required|array',
};

const customErrorMessages = {
  buName: {
    required: 'Please Add Business Unit Name',
  },
  hrBPs: {
    required: 'Please select at least one HRBP',
  },
  buHead: {
    required: 'Please select at least one BU Head',
  },
};
const validator = createFormValidator(validationRules, customErrorMessages);

const debounce = <Args extends unknown[]>(func: (...args: Args) => void, delay: number) => {
  let timer: NodeJS.Timeout;
  return (...args: Args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const AddBuHead: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data = {} } = location.state;
  const [buName, setBuName] = useState(data.title);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [alertType, setAlertType] = useState<'success' | 'info' | 'warning'>('success');
  const [alertHeader, setAlertHeader] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [selectedBuHead, setSelectedBuHead] = useState<any[]>([]);
  const [buHead, setBuHead] = useState<any[]>([]);
  const [hrBPs, setHrBPs] = useState<any[]>([]);
  const [selectedhrbps, setSelectedHrbps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [buProfiles, setBuProfiles] = useState<any[]>([]);

  async function fetchData() {
    setLoading(true);

    try {
      const [usersResponse, buProfilesResponse] = await Promise.all([
        CommHandler.request({
          url: `${config.apiBaseUrl}/users`,
          method: 'GET',
          headers: getAuthHeader,
        }),
        CommHandler.request({
          url: `${config.apiBaseUrl}/bu-profiles`,
          method: 'GET',
          headers: getAuthHeader,
        }),
      ]);
      const BuHeadList = usersResponse?.data?.map((item: any) => ({
        label: item.fullName,
        id: item?.profile?.user_id,
        email: item.email,
      }));
      setBuHead(BuHeadList);
      setHrBPs(BuHeadList);
      setBuProfiles(buProfilesResponse?.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (data.id) {
      const buHeadIdsArray = data.buHeadId ? data.buHeadId.split(',').map((id: string) => id.trim()) : [];
      const selectedBuHeadData = buHead.filter((item) => item?.id && buHeadIdsArray.includes(item.id.toString()));
      setSelectedBuHead(selectedBuHeadData.length > 0 ? selectedBuHeadData : [{ label: data.buHead, id: data.buHeadId }]);

      const hrBPsIdsArray = data.hrBPsId ? data.hrBPsId.split(',').map((id: string) => id.trim()) : [];
      const selectedHrbpData = hrBPs.filter((item) => item?.id && hrBPsIdsArray.includes(item.id.toString()));
      setSelectedHrbps(selectedHrbpData);
    }
  }, [data, buHead, hrBPs]);

  const onClose = () => {
    navigate('/bu');
  };

  const handleShowAlert = (header: string, message: string, type: 'success' | 'info' | 'warning') => {
    setAlertType(type);
    setAlertHeader(header);
    setAlertMessage(message);
    setShowAlert(true);
  };

  const handleCloseAlert = () => {
    if (alertType === 'success') {
      onClose();
    }
    setShowAlert(false);
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
    onClose();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationDialog(false);
  };

  const handleConfirmCancelConfirmation = () => {
    setShowConfirmationDialog(false);
    onClose();
  };

  const handleCancel = () => {
    if (selectedBuHead.length > 0 || buName.length > 0) {
      setShowConfirmationDialog(true);
    } else {
      onClose();
    }
  };

  const checkTitleAvailability = useMemo(
    () =>
      debounce((value: string) => {
        if (buProfiles.length === 0) {
          setIsSubmitDisabled(false);
          setFormErrors((prevErrors) => {
            const { title, ...restErrors } = prevErrors;
            return restErrors;
          });
          return;
        }
        const isTitleExists = buProfiles.some((profile) => profile.department === value);
        if (isTitleExists) {
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            title: 'This Business Unit already exist.',
          }));
          setIsSubmitDisabled(true);
        } else {
          setFormErrors((prevErrors) => {
            const { title, ...restErrors } = prevErrors;
            return restErrors;
          });
          setIsSubmitDisabled(false);
        }
      }, 500),
    [buProfiles],
  );

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const { isValid, sanitizedValue, error } = validateAndSanitizeNameWithLimitedSpecialCharacters(value, 100);
    setBuName(sanitizedValue);
    setFormErrors({
      ...formErrors,
      title: isValid ? '' : error || 'Business Unit Name should not be more than 100 characters and must use allowed characters',
    });
    if (isValid) {
      checkTitleAvailability(sanitizedValue);
    }
  };

  const handleBuHeadChange = (newValue: any[]) => {
    setSelectedBuHead(newValue);
    if (newValue.length > 0) {
      setFormErrors((prevErrors) => {
        const { buHead: buHeadErrorValue, ...restErrors } = prevErrors;
        return buHeadErrorValue !== undefined ? restErrors : prevErrors;
      });
    }
  };

  const handleHrbpChange = (newValue: any[]) => {
    setSelectedHrbps(newValue);
    if (newValue.length > 0) {
      setFormErrors((prevErrors) => {
        const { hrBPs: hrbpErrorValue, ...restErrors } = prevErrors;
        return hrbpErrorValue !== undefined ? restErrors : prevErrors;
      });
    }
  };
  const handleSave = async () => {
    try {
      // const errors = validator.validateForm({ title, buHead, hrBPs });
      const errors = validator.validateForm({
        buHead: selectedBuHead,
        hrBPs: selectedhrbps,
        buName,
      });
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
      setFormErrors({}); // Clear error messages when fields are filled with values

      setIsSubmitDisabled(true);

      const requestData: RequestData = {
        department: buName,
        buIds: selectedBuHead?.map((item) => item.id),
        hrIds: selectedhrbps?.map((item) => item.id),
      };

      if (data.id) {
        requestData.id = data?.id;
      }

      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/bu-profiles`,
        method: data.id ? 'PUT' : 'POST',
        data: requestData,
        headers: getAuthHeader,
      });

      if (response.status === 200) {
        handleShowAlert(data.id ? 'Edit Business Unit' : 'Add Business Unit', data.id ? 'Business Unit updated successfully!' : 'Business Unit added successfully!', 'success');
      } else {
        setIsSubmitDisabled(false);
        const errorMessage = response.data && response.data.message ? response.data.message : 'Some error occurred while saving!';
        console.error('Error:', errorMessage);
        handleShowAlert(data.id ? 'Edit Business Unit' : 'Add Business Unit', errorMessage, 'warning');
      }
    } catch (error) {
      setIsSubmitDisabled(false);
      console.error('Error:', error);
      handleShowAlert('Add/Edit Business Unit', 'Some error occurred!', 'warning');
    }
  };

  return (
    <AddKRAFormWrapper>
      <Card variant="outlined" className="card-wrap">
        <CardHeader title={data.id ? 'Edit Business Unit' : 'Add Business Unit'} />
        <CardContent>
          <form>
            <div>
              <Box mt={2} display="flex" justifyContent="flex-end" className="add-kra-field">
                <FormInput
                  inputId="basic-input"
                  required
                  inputBoxType="outlined"
                  label="Business Unit Name *"
                  type="input"
                  disabled={!!data.id}
                  defaultValue={buName}
                  onValueChange={handleTitleChange}
                  InputLabelShrink={false}
                  marginBottom={0}
                  error={!!formErrors.title}
                  helperText={formErrors.title}
                />
              </Box>
            </div>
            {formErrors.buName && <div style={{ color: 'red', marginTop: '5px' }}>{formErrors.buName}</div>}
            <div>
              <Box
                component="form"
                sx={{
                  '& > :not(style)': { width: '100%' },
                }}
                noValidate
                autoComplete="off"
                className="add-kra-field"
              >
                <InputLabel htmlFor="designation">BU Heads *</InputLabel>
                <FormControl fullWidth variant="outlined">
                  <Autocomplete
                    multiple
                    id="designation"
                    options={loading ? [] : buHead}
                    value={loading ? [] : selectedBuHead}
                    loading={loading}
                    onChange={(_, newValue) => handleBuHeadChange(newValue)}
                    disableCloseOnSelect
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option?.id}>
                          <Checkbox checked={selectedBuHead.includes(option)} indeterminate={false} />
                          {option?.label} ({option?.email})
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search BU Head list"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
                {formErrors.buHead && <div style={{ color: 'red', marginTop: '5px' }}>{formErrors.buHead}</div>}
              </Box>
            </div>
            <div>
              <Box
                component="form"
                sx={{
                  '& > :not(style)': { width: '100%' },
                }}
                noValidate
                autoComplete="off"
                className="add-kra-field"
              >
                <InputLabel htmlFor="designation">HRBPs *</InputLabel>
                <FormControl fullWidth variant="outlined">
                  <Autocomplete
                    multiple
                    id="designation"
                    options={loading ? [] : hrBPs}
                    value={loading ? [] : selectedhrbps}
                    loading={loading}
                    onChange={(_, newValue) => handleHrbpChange(newValue)}
                    disableCloseOnSelect
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option?.id}>
                          <Checkbox checked={selectedhrbps?.includes(option)} indeterminate={false} />
                          {option?.label} ({option?.email})
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search HRBP list"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
                {formErrors.hrBPs && <div style={{ color: 'red', marginTop: '5px' }}>{formErrors.hrBPs}</div>}
              </Box>
            </div>
            <Box mt={2} display="flex" className="admin_addnew_modal_footer" justifyContent="flex-end">
              <Stack spacing={2} direction="row">
                <Button variant="outlined" onClick={handleCancel} color={ButtonColor.inherit}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isSubmitDisabled || !!formErrors.title} sx={{ color: '#ffffff' }}>
                  {data.id ? 'Update' : 'Save'}
                </Button>
              </Stack>
            </Box>
          </form>
        </CardContent>
        <Dialog className="btn_style_prt" open={showConfirmationDialog}>
          <DialogTitle>Are you sure?</DialogTitle>
          <DialogContent>
            <DialogContentText>You have unsaved changes. Are you sure you want to cancel?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelConfirmation}>No</Button>
            <Button onClick={handleConfirmCancelConfirmation}>Yes</Button>
          </DialogActions>
        </Dialog>
        {showSuccessAlert && (
          <Alert onClose={handleCloseSuccessAlert} severity="success">
            <AlertTitle>Success</AlertTitle>
            {data.id ? 'Business Unit updated successfully.' : 'Business Unit added successfully.'}
            <Button variant="outlined" size="small" onClick={handleCloseSuccessAlert}>
              Close
            </Button>
          </Alert>
        )}
      </Card>
      <MessageAlert open={showAlert} onClose={handleCloseAlert} header={alertHeader || 'Alert'} message={alertMessage || 'An error occurred.'} alertType={alertType} />
    </AddKRAFormWrapper>
  );
};

export default AddBuHead;
